import {Component, OnDestroy, OnInit} from '@angular/core';
import {ROUTES} from '../../app.routes';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ASecureForm} from '../../sdk/interface/form/ASecureForm';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {AlertService} from '../../sdk/service/alert.service';
import {EnvironmentService} from '../../sdk/service/environment.service';
import {EcommerceVerificationService} from '../../sdk/service/ecommerce-verification.service';
import {EcommerceVerificationState} from '../../sdk/interface/ecommerce-verification-state';

/**
 * The home component acts as an informal 'router' when a user tries to access the site root.
 * Based on the current users permissions, the home page may differ. This component is
 * responsible for redirecting the user to the correct home page based on their permissions.
 */
@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent extends ASecureForm implements OnInit, OnDestroy
{
    private readonly destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private ecommerceVerificationService: EcommerceVerificationService)
    {
        super(authenticationService, environmentService);
    }

    ngOnInit()
    {
        /* This code will route the user to the correct 'home screen' based on their allowed
         permissions */
        if (this.hasRouteAccess(ROUTES.SECURE.ACCOUNT.LIST))
        {
            const userLogin = this.authenticationService.getUserLogin();
            if (userLogin.eCommerceMerchant && this.environmentService.eCommerceEnabled)
            {
                this.routeEcommerceMerchant();
            }
            else
            {
                this.router.navigate([ROUTES.SECURE.ACCOUNT.LIST.path]).then();
            }
        }
        else
        {
            // fields agents should only be able to make request to pay transactions
            this.router.navigate([ROUTES.SECURE.ACCOUNT.CREATE.path]).then();
        }
    }

    ngOnDestroy()
    {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Routes an e-commerce merchant based on their KYC status and role: merchants who still need to
     * complete verification land on the ecommerce landing page, everyone else on the ecommerce
     * dashboard. Only invoked when e-commerce is enabled; if the verification state cannot be loaded,
     * the merchant falls back to the onboarding landing page (which re-checks the state and redirects
     * a verified merchant to the dashboard).
     *
     * Users without BUSINESS_UPDATE_FICA_DETAILS (Admins, Field Agents) cannot complete KYC, so they skip the
     * verification check and go to their e-commerce home.
     */
    private routeEcommerceMerchant(): void
    {
        if (!this.hasRouteAccess(ROUTES.SECURE.BUSINESS.EDIT_FICA_DETAILS))
        {
            this.router.navigate([this.resolveEcommerceDashboardPath()]).then();
            return;
        }

        this.ecommerceVerificationService.getVerificationState().pipe(takeUntil(this.destroy$)).subscribe({
            next: (verificationState: EcommerceVerificationState) =>
            {
                const homePath = this.resolveEcommerceHomePath(verificationState);
                this.router.navigate([homePath]).then();
            },
            error: (error) =>
            {
                // unknown KYC behaves like not-started, so route to the onboarding landing page; it
                // re-checks the state and redirects a verified merchant to the dashboard.
                this.debugOutput('Failed to load verification state for ecommerce routing', error);
                this.router.navigate([ROUTES.SECURE.ECOMMERCE.LANDING.path]).then();
            }
        });
    }

    /**
     * Chooses the ecommerce merchant's home path. Merchants still in verification go to the onboarding landing page,
     * everyone else to their dashboard.
     */
    private resolveEcommerceHomePath(verificationState: EcommerceVerificationState): string
    {
        if (this.ecommerceVerificationService.shouldDisplayLandingPage(verificationState, this.isGuestAdminUser()))
        {
            return ROUTES.SECURE.ECOMMERCE.LANDING.path;
        }
        return this.resolveEcommerceDashboardPath();
    }

    /**
     * The ecommerce dashboard, but only if the user actually holds {@code ECOMMERCE_DASHBOARD_READ}; without it the
     * dashboard's route guard would bounce them straight back here and loop, so we fall back to the account list.
     */
    private resolveEcommerceDashboardPath(): string
    {
        return this.hasRouteAccess(ROUTES.SECURE.ACCOUNT.ECOMMERCE_DASHBOARD)
            ? ROUTES.SECURE.ACCOUNT.ECOMMERCE_DASHBOARD.path
            : ROUTES.SECURE.ACCOUNT.LIST.path;
    }
}